import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";

import { H2, H1, P } from "../../../AbstractElements";
import {
  ALL_COURSE_FILTER_STATUS,
  courseTypeMap,
  sourceTypeMap,
} from "../../../Api/constants";
import CourseSummary from "../../../CommonElements/CourseSummaryCard";
import FilterSliderModal from "../../../CommonElements/FilterSliderModal";
import CommonModal from "../../../CommonElements/Modal";
import MultiProgressBar from "../../../CommonElements/MultiProgressBar";
import SearchBar from "../../../CommonElements/SearchBar";
import YearNavigator from "../../../CommonElements/YearNavigator";
import LearningPlanCourseCard, {
  LearningPlanCardData,
} from "../../../container/LearningPlanCourseCard";
import NoData from "../../../container/NoData";
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";
import { updateCourseStatus } from "../../../ReduxToolkit/Reducers/CourseSlice";
import {
  getILPCourse,
  getOverallStatistics,
} from "../../../ReduxToolkit/Reducers/LearningPlanSlice";
import {
  CourseType,
  ILT,
  MarkAsCompleted,
  MarkAsCompletedConfirmation,
  NoDataText,
  Select,
  SelfEnrolledStatistics,
  Submit,
  VILT,
  WBT,
  Webinar,
} from "../../../utils/Constant";
import {
  courseFilterDropdownList,
  calculateValuePercentage,
  multiProgressData,
  ilpCourseSummaryData,
  getOngoingFiscalYear,
} from "../../../utils/helper/helper";

const ILPSelfEnrollment = ({ userId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { ilpCourseList, loading } = useAppSelector(
    state => state.learningPlan
  );
  const { overallStatistics } = useAppSelector(state => state.learningPlan);

  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<LearningPlanCardData[]>(
    []
  );
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [allMappedCourses, setAllMappedCourses] = useState<
    LearningPlanCardData[]
  >([]);
  const [clearData, setClearData] = useState(false);
  const [fyYear, setFyYear] = useState(getOngoingFiscalYear());

  useEffect(() => {
    fetchCourses();
  }, [dispatch, fyYear, selectedItems]);

  const toggleActionModal = () => {
    if (isActionModalOpen) {
      setSelectedCourseId("");
      setSelectedOption("");
    }
    setIsActionModalOpen(!isActionModalOpen);
  };

  const onDropdownClick = (id: string, name: string) => {
    setSelectedCourseId(id);
    setSelectedOption(name);
    toggleActionModal();
  };

  const fetchCourses = async () => {
    const courseType =
      selectedItems.length > 0 ? selectedItems : ALL_COURSE_FILTER_STATUS;
    dispatch(getOverallStatistics({ selfEnrollment: true, userId: userId }));
    await dispatch(
      getILPCourse({
        courseType: courseType,
        selfEnrollment: true,
        fyYear: fyYear,
        userId: userId,
      })
    );
  };

  useEffect(() => {
    setAllMappedCourses([]);
    const mappedCourseData = ilpCourseList?.mapped_courses?.map(course => {
      const domainNames =
        ilpCourseList?.domain[course?.course_id]?.map(
          domainItem => domainItem.name
        ) || [];

      return {
        id: course.id,
        courseId: course.course_id.toString(),
        courseName: course.course_name,
        domainName: domainNames,
        status: course.status,
        badgeTextColor: false,
        scheduledOn: course.created_at,
        completedOn: null,
        assignedBy: sourceTypeMap[course.source_type],
        courseType: courseTypeMap[course.course_type],
      };
    });

    setAllMappedCourses(mappedCourseData);
    setSearchResults(mappedCourseData);
  }, [ilpCourseList]);

  const handleSearch = (text: string) => {
    setClearData(false);
    const filteredCourses = allMappedCourses?.filter(course =>
      course.courseName.toLowerCase().includes(text.toLowerCase())
    );
    setSearchResults(filteredCourses);
  };

  const handleDone = (values: { [key: string]: string[] }) => {
    setClearData(true);
    if (values["course_type"]) {
      const newSelectedItems = values["course_type"];
      setSelectedItems(newSelectedItems);
      fetchCourses();
    }
  };

  const handleClear = () => {
    setClearData(true);
    setSelectedFilterValues({});
    setDefaultFilterValues({});
    setSelectedItems([]);
    fetchCourses();
  };

  const handleYearChange = (year: string) => {
    setClearData(true);
    setFyYear(year);
  };

  const handleClick = item => {
    const baseUrl = `/my-learning-plan/course-details?course_id=${item.courseId}`;
    const url = userId ? `${baseUrl}&user_id=${userId}` : baseUrl;
    navigate(url);
  };

  const renderCourses = () => {
    if (loading) {
      return null;
    }
    if (
      ilpCourseList?.mapped_courses?.length === 0 ||
      searchResults?.length === 0
    ) {
      return (
        <NoData svg="empty-folder-icon" title={NoDataText} showCard={false} />
      );
    }

    return (
      <LearningPlanCourseCard
        onDropdownClick={onDropdownClick}
        showDropDown={true}
        handleClick={handleClick}
        data={searchResults}
        showUserDetails={userId ? true : false}
      />
    );
  };

  const renderModalBody = () => {
    if (selectedOption === MarkAsCompleted) {
      return <P>{MarkAsCompletedConfirmation}</P>;
    }
  };

  const handlePrimaryClick = async () => {
    if (selectedOption === MarkAsCompleted) {
      const payload = {
        course_id: parseInt(selectedCourseId),
        user_id: userId,
        status: "completed",
        type: "ilp",
      };
      await dispatch(
        updateCourseStatus({
          courseStatus: payload,
        })
      );
    }

    fetchCourses();
    toggleActionModal();
  };

  return (
    <>
      <CommonModal
        sizeTitle={selectedOption}
        modalBodyClassName=""
        isOpen={isActionModalOpen}
        toggle={toggleActionModal}
        backdrop="static"
        size="lg"
        showFooter
        onPrimaryBtnClick={handlePrimaryClick}
        primaryBtnText={Submit}
      >
        {renderModalBody()}
      </CommonModal>
      <Card className="p-2 mb-3">
        <CardBody>
          <Row className="justify-content-center gap-5 gap-lg-0">
            <Col className="mt-3" lg={4} xs={12}>
              <H2 className="text-black">{SelfEnrolledStatistics}</H2>
              <H1 className="mt-1 custom-overall-percentage">
                {overallStatistics &&
                  `${calculateValuePercentage(
                    overallStatistics.completed_courses,
                    overallStatistics.total_courses
                  )}%`}
              </H1>
              <div className="mt-2">
                {overallStatistics?.total_courses > 0 && (
                  <MultiProgressBar
                    progressBars={multiProgressData(overallStatistics)}
                  />
                )}
              </div>
            </Col>
            <Col lg={8} xs={12}>
              {overallStatistics && (
                <CourseSummary data={ilpCourseSummaryData(overallStatistics)} />
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>
      <Card className="p-1 mb-3">
        <CardBody>
          <Row className="align-items-center gap-lg-0 gap-3">
            <Col
              sm="12"
              md="12"
              lg="4"
              className="d-flex justify-content-start gap-3"
            >
              <span className="text-gray d-flex gap-1">
                {ILT} :
                <strong className="text-black">
                  {ilpCourseList?.course_type_count?.ilt}
                </strong>
              </span>
              <span className="text-gray d-flex gap-1">
                {VILT} :
                <strong className="text-black">
                  {ilpCourseList?.course_type_count?.vilt}
                </strong>
              </span>
              <span className="text-gray d-flex gap-1">
                {Webinar} :
                <strong className="text-black">
                  {ilpCourseList?.course_type_count?.webinar}
                </strong>
              </span>
              <span className="text-gray d-flex gap-1">
                {WBT} :
                <strong className="text-black">
                  {ilpCourseList?.course_type_count?.wbt}
                </strong>
              </span>
            </Col>
            <Col
              sm="12"
              lg="8"
              md="12"
              className="d-flex flex-column flex-lg-row justify-content-start justify-content-lg-end"
            >
              <Row className="justify-content-center align-items-center">
                <Col
                  lg="7"
                  md="6"
                  sm="12"
                  className="d-flex gap-3 align-items-center"
                >
                  <SearchBar
                    showSearch
                    onSearch={handleSearch}
                    clearOnProp={clearData}
                  />
                  <div>
                    <FilterSliderModal
                      dropdowns={[
                        {
                          label: CourseType,
                          key: "course_type",
                          tooltipText: `${Select} ${CourseType}`,
                          options: courseFilterDropdownList,
                          isMultiSelect: true,
                        },
                      ]}
                      selectedFilterValues={selectedFilterValues}
                      defaultFilterValues={defaultFilterValues}
                      setSelectedFilterValues={setSelectedFilterValues}
                      setDefaultFilterValues={setDefaultFilterValues}
                      onDone={handleDone}
                      onClear={handleClear}
                    />
                  </div>
                </Col>
                <Col lg="5" md="6" sm="12">
                  <div>
                    <YearNavigator
                      pastYears={20}
                      futureYears={20}
                      onYearChange={handleYearChange}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {renderCourses()}
    </>
  );
};

ILPSelfEnrollment.propTypes = {
  userId: PropTypes.number,
};
export default ILPSelfEnrollment;
